import { useEffect, useState } from "react";
import { api, fmtINR } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Area, AreaChart } from "recharts";

export default function Reports() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports").then(r => setData(r.data)); }, []);
  if (!data) return <div className="p-10 text-white/40">Loading…</div>;

  return (
    <>
      <PageHeader title="Reports & Analytics" subtitle="Business intelligence" />
      <div className="p-8 space-y-4 fade-up">
        <div className="hairline rounded-lg bg-[#0F0F0F] p-5">
          <div className="text-sm font-semibold mb-4">Monthly Revenue & Profit</div>
          <div className="w-full h-[300px]">
            <ResponsiveContainer>
              <AreaChart data={data.monthly}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="prof" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#D97706" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#D97706" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 10 }} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} tickLine={false} />
                <YAxis tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 10 }} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} tickLine={false} />
                <Tooltip contentStyle={{ background: "#0F0F0F", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, fontSize: 11 }} />
                <Area type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2} fill="url(#rev)" />
                <Area type="monotone" dataKey="profit" stroke="#D97706" strokeWidth={2} fill="url(#prof)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="hairline rounded-lg bg-[#0F0F0F]">
            <div className="p-4 hairline-b text-sm font-semibold">Top Clients</div>
            <div className="divide-y divide-white/5">
              {data.top_clients.map((c, i) => (
                <div key={c.id} className="flex items-center gap-3 p-4">
                  <div className="mono text-lg font-bold text-white/30 w-6">{i + 1}</div>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{c.name}</div>
                    <div className="text-[11px] text-white/50">{c.company} · {c.projects} projects</div>
                  </div>
                  <div className="mono text-sm text-emerald-400">{fmtINR(c.revenue)}</div>
                </div>
              ))}
              {data.top_clients.length === 0 && <div className="p-6 text-white/40 text-sm">No data.</div>}
            </div>
          </div>
          <div className="hairline rounded-lg bg-[#0F0F0F]">
            <div className="p-4 hairline-b text-sm font-semibold">Top Freelancers</div>
            <div className="divide-y divide-white/5">
              {data.top_freelancers.map((f, i) => (
                <div key={f.id} className="flex items-center gap-3 p-4">
                  <div className="mono text-lg font-bold text-white/30 w-6">{i + 1}</div>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{f.name}</div>
                    <div className="text-[11px] text-white/50">{f.projects} projects</div>
                  </div>
                  <div className="mono text-sm text-[#D97706]">{fmtINR(f.earned)}</div>
                </div>
              ))}
              {data.top_freelancers.length === 0 && <div className="p-6 text-white/40 text-sm">No data.</div>}
            </div>
          </div>
        </div>

        <div className="hairline rounded-lg bg-[#0F0F0F] p-5">
          <div className="text-sm font-semibold mb-3">Payment Status</div>
          <div className="grid grid-cols-3 gap-3">
            <div className="hairline rounded-md p-4 bg-emerald-500/5 border-emerald-500/20">
              <div className="text-[10px] uppercase tracking-widest text-emerald-400">Paid</div>
              <div className="mono text-2xl font-bold text-emerald-400 mt-1">{data.payment_status.paid}</div>
            </div>
            <div className="hairline rounded-md p-4 bg-amber-500/5 border-amber-500/20">
              <div className="text-[10px] uppercase tracking-widest text-amber-400">Partial</div>
              <div className="mono text-2xl font-bold text-amber-400 mt-1">{data.payment_status.partial}</div>
            </div>
            <div className="hairline rounded-md p-4 bg-red-500/5 border-red-500/20">
              <div className="text-[10px] uppercase tracking-widest text-red-400">Unpaid</div>
              <div className="mono text-2xl font-bold text-red-400 mt-1">{data.payment_status.unpaid}</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
